import { CSNotification } from "@today/api/tracker"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef, useState } from "react"
import { FaBell, FaRegBell } from "react-icons/fa6"
import {
  useNotificationChannel,
  useNotificationList,
  useBulkReadNotifications,
} from "./useNotifications"
import { NotificationItem, NotificationItemSkeleton } from "./NotificationItem"

export function NotificationDropdown() {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { unreadCount, mutateChannel } = useNotificationChannel()
  const {
    notifications: unreadNotifs,
    isLoading: isLoadingUnread,
    mutateNotifications: mutateUnread,
  } = useNotificationList({ unread: true })
  const {
    notifications: allNotifs,
    isLoading: isLoadingAll,
    mutateNotifications: mutateAll,
  } = useNotificationList()
  const { bulkRead } = useBulkReadNotifications()

  const notifications = unreadNotifs.length > 0 ? unreadNotifs : allNotifs
  const isLoading =
    isLoadingUnread || (unreadNotifs.length === 0 && isLoadingAll)
  const mutateNotifications = useCallback(
    (...args: Parameters<typeof mutateUnread>) => {
      mutateUnread(...args)
      mutateAll(...args)
    },
    [mutateUnread, mutateAll]
  )

  // 바깥 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  // 드롭다운 열릴 때 목록 갱신
  useEffect(() => {
    if (isOpen) {
      mutateNotifications()
    }
  }, [isOpen, mutateNotifications])

  const handleNotificationClick = useCallback(
    async (notification: CSNotification) => {
      setIsOpen(false)

      // Optimistic update
      if (!notification.isRead) {
        mutateNotifications(
          (prev) =>
            prev
              ? {
                  ...prev,
                  notifications: prev.notifications.map((n) =>
                    n.id === notification.id ? { ...n, isRead: true } : n
                  ),
                }
              : prev,
          false
        )
        mutateChannel(
          (prev) =>
            prev
              ? { ...prev, unreadCount: Math.max(0, prev.unreadCount - 1) }
              : prev,
          false
        )

        try {
          await bulkRead({ notificationIds: [notification.id] })
        } catch {
          // 조용히 롤백
          mutateNotifications()
          mutateChannel()
        }
      }

      // CS 알림 → /cs/tickets?ticketId=xxx
      const ticketId = notification.metadata.ticketId
      if (ticketId) {
        router.push(`/cs/tickets?ticketId=${ticketId}`)
      }
    },
    [bulkRead, mutateChannel, mutateNotifications, router]
  )

  const handleMarkAllRead = useCallback(async () => {
    const unreadNotifications = notifications.filter((n) => !n.isRead)
    if (unreadNotifications.length === 0) return

    const ids = unreadNotifications.map((n) => n.id)
    const oldestId = ids[ids.length - 1]
    const newestId = ids[0]

    // Optimistic update
    mutateNotifications(
      (prev) =>
        prev
          ? {
              ...prev,
              notifications: prev.notifications.map((n) => ({
                ...n,
                isRead: true,
              })),
            }
          : prev,
      false
    )
    mutateChannel(
      (prev) =>
        prev
          ? {
              ...prev,
              unreadCount: Math.max(
                0,
                prev.unreadCount - unreadNotifications.length
              ),
            }
          : prev,
      false
    )

    try {
      await bulkRead({
        notificationIdRange: [oldestId, newestId],
      })
    } catch {
      // 조용히 롤백
      mutateNotifications()
      mutateChannel()
    }
  }, [notifications, bulkRead, mutateChannel, mutateNotifications])

  const badgeText = unreadCount > 99 ? "99+" : String(unreadCount)

  return (
    <div ref={dropdownRef} className="relative">
      {/* 벨 아이콘 */}
      <button
        className="relative flex items-center justify-center p-1 text-gray-600 hover:text-gray-900"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {unreadCount > 0 ? <FaBell size={18} /> : <FaRegBell size={18} />}
        {unreadCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-[10px] font-bold leading-none text-white">
            {badgeText}
          </span>
        )}
      </button>

      {/* 드롭다운 패널 */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          {/* 헤더 */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <span className="text-sm font-semibold text-gray-900">
              알림 센터
            </span>
            <button
              className="text-xs text-blue-600 hover:text-blue-800 disabled:cursor-not-allowed disabled:text-gray-400"
              onClick={handleMarkAllRead}
              disabled={!notifications.some((n) => !n.isRead)}
            >
              모두 읽음
            </button>
          </div>

          {/* 알림 리스트 */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <>
                <NotificationItemSkeleton />
                <NotificationItemSkeleton />
                <NotificationItemSkeleton />
              </>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <FaRegBell size={24} />
                <p className="mt-2 text-sm">새로운 알림이 없습니다</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                />
              ))
            )}
          </div>

          {/* 전체 보기 */}
          <div className="border-t border-gray-200">
            <button
              className="w-full py-3 text-center text-sm text-blue-600 hover:bg-gray-50"
              onClick={() => {
                setIsOpen(false)
                router.push("/notifications")
              }}
            >
              전체 보기
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
